import React from "react"
import { Button, Modal, Form, Col, InputGroup } from "react-bootstrap"
import { toast } from "react-toastify"
import { Subscription } from "rxjs"
import ActionButton from "../ActionButton"
import StateSelect from "../FormattedInputs/StateSelect"
import { CompanyActivationObservable, PortalActivationObservable } from "../../api/Administration/ActivationProcess"
import ImplementationSearchResult from "../../api/Invitation/Model/ImplementationSearchResult"
import { CompanyDataObservable, ImplementationSearchObservable } from "../../api/Invitation/InformationGet"
import CompanyData from "../../api/Companies/CompanyModel/CompanyData"
import { StateObservable } from "../../api/OrderRequests/OrderRequestsConstraintGet"
import State from "../../api/OrderRequests/OrderRequestsModel/State"
import { RouteComponentProps } from "react-router"
import { Home } from "../../config/Routes"

export enum ActivationType {
  Portal = "portal",
  Company = "company"
}

interface EntityActivationProps {
  type: ActivationType
}

interface EntityActivationState {
  isPortal: boolean
  itemSaving: boolean
  itemLoading: boolean
  instanceName: string
  isNameValid: boolean
  selectedItem?: ImplementationSearchResult | CompanyData
  states: State[]
}

export default class EntityActivation extends React.Component<EntityActivationProps & RouteComponentProps, EntityActivationState> {
  emptyState: EntityActivationState = {
    isPortal: this.props.type === ActivationType.Portal,
    itemSaving: false,
    itemLoading: false,
    instanceName: "",
    isNameValid: false,
    selectedItem: undefined,
    states: []
  }
  state: EntityActivationState = { ...this.emptyState }

  saveSubscription = new Subscription()
  loadSubscription = new Subscription()

  componentDidMount() {
    this.loadSubscription.add(
      StateObservable().subscribe(
        (results: State[]) => this.setState({ states: results }),
        (error) => {
          toast.error(error?.errorData?.message || error.message)
          this.setState({ states: [] })
        }
      )
    )
  }

  componentWillUnmount() {
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.loadSubscription && this.loadSubscription.unsubscribe()
  }

  handleCancel = () => (this.props as RouteComponentProps).history?.push(Home)

  handleActivate = () => {
    this.setState({ itemSaving: true })
    const { selectedItem, isPortal } = this.state

    if (isPortal) {
      this.saveSubscription = PortalActivationObservable(selectedItem?.ImplementationID || 0).subscribe(
        (data) => toast.success(data),
        (error) => {
          toast.error(error?.errorData?.message || error.message)
          this.setState({ itemSaving: false })
        },
        () => this.setState({ ...this.emptyState })
      )
    }
    else {
      this.saveSubscription = CompanyActivationObservable(selectedItem?.ImplementationID || 0, selectedItem?.AccountID || 0).subscribe(
        (data) => toast.success(data),
        (error) => {
          toast.error(error?.errorData?.message || error.message)
          this.setState({ itemSaving: false })
        },
        () => this.setState({ ...this.emptyState })
      )
    }
  }

  handleRetrieve = () => {
    this.setState({ itemLoading: true, selectedItem: undefined })
    const { instanceName, isPortal } = this.state

    if (isPortal) {
      this.loadSubscription = ImplementationSearchObservable(instanceName, true).subscribe(
        (data: ImplementationSearchResult[]) => this.setState({ selectedItem: data[0] }),
        (error) => {
          toast.error(error?.errorData?.message || error.message)
          this.setState({ itemLoading: false })
        },
        () => this.setState({ itemLoading: false })
      )
    }
    else {
      this.loadSubscription = CompanyDataObservable(instanceName, true).subscribe(
        (data: CompanyData[]) => this.setState({ selectedItem: data[0] }),
        (error) => {
          toast.error(error?.errorData?.message || error.message)
          this.setState({ itemLoading: false })
        },
        () => this.setState({ itemLoading: false })
      )
    }
  }

  isNameValid = (value: string) => {
    if (value.length < 3) {
      return false
    }
    return true
  }

  render() {
    const { selectedItem, itemSaving, itemLoading, isPortal } = this.state

    return (
      <Modal.Dialog size="xl">
        <Modal.Header>
          <Modal.Title className="text-uppercase">{isPortal ? "Activate Portal" : "Activate Company"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Row className="form-group required">
              <Form.Label column lg={3} htmlFor="companyName">{isPortal ? "Clinic Name" : "Company Name"}</Form.Label>
              <Col lg={9}>
                <InputGroup>
                  <Form.Control id="companyName" type="text" value={this.state.instanceName} className="bg-white" onChange={(e) => { this.setState({ instanceName: e.target.value, isNameValid: this.isNameValid(e.target.value) }) }} />
                  <InputGroup.Append>
                    <ActionButton variant="secondary" onClick={this.handleRetrieve} disabled={!this.state.isNameValid} spin={itemLoading} disableOnSpin>Retrieve</ActionButton>
                  </InputGroup.Append>
                </InputGroup>
              </Col>
            </Form.Row>
            <Form.Row className="form-group">
              <Form.Label column lg={3} htmlFor="companyCode">{isPortal ? "Clinic Lab Code/ID" : "Company Code/ID"}</Form.Label>
              <Col lg={9}>
                <Form.Control id="companyCode" type="text" disabled={true} defaultValue="" />
              </Col>
            </Form.Row>
            <Form.Row className="form-group">
              <Form.Label column lg={3} htmlFor="streetAddress2">Street Address 1</Form.Label>
              <Col lg={9}>
                <Form.Control id="streetAddress1" type="text" disabled={true} defaultValue={selectedItem?.Address1} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group">
              <Form.Label column lg={3} htmlFor="streetAddress2">Street Address 2</Form.Label>
              <Col lg={9}>
                <Form.Control id="streetAddress2" type="text" disabled={true} defaultValue={selectedItem?.Address2} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group">
              <Form.Label column lg={3} htmlFor="city">City</Form.Label>
              <Col lg={9}>
                <Form.Control id="city" type="text" disabled={true} defaultValue={selectedItem?.City} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group">
              <Form.Label column lg={3} htmlFor="state">State</Form.Label>
              <Col sm={6}>
                <StateSelect selectedCode={this.state.states.find(item => item.StateProvinceTerritoryID === selectedItem?.StateProvinceTerritoryID)?.Abbreviation} states={this.state.states} isDisabled={true} onChange={() => { }} />
              </Col>
              <Form.Label column sm={1} htmlFor="zip">ZIP</Form.Label>
              <Col sm={2}>
                <Form.Control id="zip" type="text" defaultValue={selectedItem?.Zip} disabled={true} />
              </Col>
            </Form.Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <ActionButton variant="primary" onClick={this.handleActivate} disabled={!selectedItem} spin={itemSaving} disableOnSpin>Activate</ActionButton>
          <Button variant="secondary" className="mr-2" onClick={this.handleCancel}>Cancel</Button>
        </Modal.Footer>
      </Modal.Dialog>
    )
  }
}
